import { DailySchedule, IProgramSetup, TimeSlot } from 'shared/interfaces/IProgramSetup';

export type ScheduleMode = 'tv' | 'radio';
export type DayKey = keyof DailySchedule;
export type ChangeScope = 'one' | 'all';

export const DAY_KEYS: DayKey[] = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
export const DAY_NAMES = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
export const DAY_MINUTES = 1440;
export const SNAP_MINUTES = 5;
// What the Program Setup form stores for a day that is switched off.
export const DEFAULT_SLOT = '1990-01-01 00:00:00';

/** A day's airing, in minutes from midnight. `end <= start` means it runs past midnight. */
export interface Slot {
  start: number;
  end: number;
}

/** A proposed new airing for one occurrence, from a drag on the guide. */
export interface ScheduleChange {
  day: number;
  start: number;
  end: number;
}

export interface OverlapPair {
  day: number;
  a: IProgramSetup;
  b: IProgramSetup;
}

const pad = (n: number) => String(n).padStart(2, '0');
const wrap = (min: number) => ((min % DAY_MINUTES) + DAY_MINUTES) % DAY_MINUTES;

/** Reads the HH:mm out of any stored timeslot shape: `1990-01-01 05:30:00`, `05:30`, ISO, ... */
export const toMinutes = (value?: string | null): number | null => {
  if (!value) return null;
  const match = /(\d{1,2}):(\d{2})/.exec(value);
  if (!match) return null;
  return Number(match[1]) * 60 + Number(match[2]);
};

export const hhmm = (min: number) => {
  const m = wrap(min);
  return `${pad(Math.floor(m / 60))}:${pad(m % 60)}`;
};

/** Minutes → the timeslot string the create/update API expects. */
export const toTimeslot = (min: number) => `1990-01-01 ${hhmm(min)}:00`;

export const fmt12 = (min: number) => {
  const m = wrap(min);
  const h = Math.floor(m / 60);
  return `${h % 12 === 0 ? 12 : h % 12}:${pad(m % 60)} ${h < 12 ? 'AM' : 'PM'}`;
};

export const fmtRange = (slot: Slot) => `${fmt12(slot.start)} – ${fmt12(slot.end)}`;

/** The day's airing, or null when the day is off (no times, or a zero-length slot such as 00:00–00:00). */
export const slotFromTimeslot = (timeslot?: TimeSlot | null): Slot | null => {
  const start = toMinutes(timeslot?.timeslot_start);
  const end = toMinutes(timeslot?.timeslot_end);
  if (start === null || end === null || start === end) return null;
  return { start, end };
};

export const slotOf = (record: IProgramSetup, day: number): Slot | null =>
  slotFromTimeslot(record.daily_schedule?.[DAY_KEYS[day]]);

/** Overnight airings split at midnight so each piece fits on one day row. */
export const spanOf = (slot: Slot): [number, number][] =>
  slot.end <= slot.start
    ? ([
        [slot.start, DAY_MINUTES],
        [0, slot.end],
      ].filter(([a, b]) => b > a) as [number, number][])
    : [[slot.start, slot.end]];

const slotsOverlap = (x: Slot, y: Slot) =>
  spanOf(x).some(([a0, a1]) => spanOf(y).some(([b0, b1]) => a0 < b1 && b0 < a1));

/**
 * Two DIFFERENT programs on the SAME channel whose times intersect on the same day are a conflict.
 * A simulcast of the same programme on another channel is a different channel entirely, so it is
 * never in this list and never counted.
 */
export const overlapPairs = (records: IProgramSetup[]): OverlapPair[] => {
  const out: OverlapPair[] = [];
  for (let d = 0; d < 7; d += 1) {
    for (let i = 0; i < records.length; i += 1) {
      const si = slotOf(records[i], d);
      if (si) {
        for (let j = i + 1; j < records.length; j += 1) {
          const sj = slotOf(records[j], d);
          if (sj && slotsOverlap(si, sj)) out.push({ day: d, a: records[i], b: records[j] });
        }
      }
    }
  }
  return out;
};

/** Days (0 = Monday) a schedule airs on. */
export const airingDaysOf = (schedule?: DailySchedule | null): number[] =>
  DAY_KEYS.reduce<number[]>((days, key, i) => (slotFromTimeslot(schedule?.[key]) ? [...days, i] : days), []);

/**
 * Returns a new daily schedule with the change applied:
 * - `all`: every airing day takes the new times (only when the day itself does not change),
 * - a move to another day: the old day is switched off and the new day takes the times,
 * - otherwise: only that day changes.
 * Automation flags are kept; a moved airing takes its automation flag with it.
 */
export const applyChange = (
  schedule: DailySchedule | undefined | null,
  fromDay: number,
  next: ScheduleChange,
  scope: ChangeScope,
): DailySchedule => {
  const out = {} as DailySchedule;
  DAY_KEYS.forEach((key) => {
    out[key] = { ...(schedule?.[key] || {}) };
  });
  const setTimes = (day: number) => {
    const key = DAY_KEYS[day];
    out[key] = { ...out[key], timeslot_start: toTimeslot(next.start), timeslot_end: toTimeslot(next.end) };
  };

  if (next.day !== fromDay) {
    const fromKey = DAY_KEYS[fromDay];
    const carriedAutomation = out[fromKey].automation_enabled ?? false;
    out[fromKey] = { timeslot_start: DEFAULT_SLOT, timeslot_end: DEFAULT_SLOT, automation_enabled: false };
    setTimes(next.day);
    out[DAY_KEYS[next.day]].automation_enabled = carriedAutomation;
  } else if (scope === 'all') {
    airingDaysOf(schedule).forEach(setTimes);
  } else {
    setTimes(fromDay);
  }
  return out;
};

/** Would this change create an overlap that does not exist today? */
export const overlapPreview = (
  records: IProgramSetup[],
  record: IProgramSetup,
  fromDay: number,
  next: ScheduleChange,
  scope: ChangeScope,
) => {
  const before = overlapPairs(records).length;
  const trial = records.map((r) =>
    r._id === record._id ? { ...r, daily_schedule: applyChange(r.daily_schedule, fromDay, next, scope) } : r,
  );
  const after = overlapPairs(trial);
  return {
    added: after.length - before,
    pairs: after.filter((pair) => pair.a._id === record._id || pair.b._id === record._id),
  };
};

export const nameOf = (record: IProgramSetup, mode: ScheduleMode) =>
  (mode === 'tv' ? record.program_details?.program_name : record.station_program_details?.station_program_name) ||
  'Untitled program';

export const channelOf = (record: IProgramSetup, mode: ScheduleMode) =>
  (mode === 'tv' ? record.channel_details?.channel_name : record.station_details?.station_name) || '';

/** The program record id the Program Type lives on. */
export const programIdOf = (record: IProgramSetup, mode: ScheduleMode) =>
  mode === 'tv' ? record.program_details?._id : record.station_program_details?._id;

/** TV programs simulcast on a radio station carry station details (and the reverse on Radio). */
export const isSimulcast = (record: IProgramSetup, mode: ScheduleMode) =>
  mode === 'tv' ? !!record.station_details?._id : !!record.channel_details?._id;

/** Where the simulcast airs, for the tooltip. */
export const simulcastOf = (record: IProgramSetup, mode: ScheduleMode) =>
  (mode === 'tv' ? record.station_details?.station_name : record.channel_details?.channel_name) || 'Yes';
